import * as React from 'react';
import { Link, RouteComponentProps } from 'react-router-dom';
import { AppActionCreators } from '@nkr/store/index';
import { useAppHookContext } from '@nkr/utils/rest-api/useAppHookContext';
import { ApiStatus } from '@nkr/utils/api/api-client';
import type { ApiResponse } from '@nkr/utils/api/api-client';
import { epletValidationApi, invalidateEpletSummaries, EpletAllele } from '@nkr/models/hla/validation/eplet';

// Losing most of an eplet's links usually means a misread residue, not real drift, so confirm twice.
const BULK_REMOVAL_WARNING_SHARE = 0.5;

const reasonFor = (allele: EpletAllele): string => {
    if (!allele.isMatchedOnGene && !allele.isMatchedOnResidue) return 'Wrong gene, residue absent';
    if (!allele.isMatchedOnGene) return 'Wrong gene for this group';

    return 'Residue not present in sequence';
};

const AlleleTable = ({ alleles, showReason }: { alleles: EpletAllele[]; showReason?: boolean }) => (
    <table className='table table-condensed'>
        <thead>
            <tr>
                <th>Allele</th>
                {showReason && <th>Why it is flagged</th>}
            </tr>
        </thead>
        <tbody>
            {alleles.map(allele => (
                <tr key={allele.id}>
                    <td><code>{allele.hlaString}</code></td>
                    {showReason && <td className='text-danger'>{reasonFor(allele)}</td>}
                </tr>
            ))}
        </tbody>
    </table>
);

export const EpletValidationDetail = ({ match }: RouteComponentProps<{ id: string }>) => {
    const parsed = Number(match.params.id);
    const epletId = Number.isFinite(parsed) ? parsed : null;

    const { dispatch } = useAppHookContext();
    const detail = epletValidationApi.useGetById(epletId);
    const createLinks = epletValidationApi.useCreateLinks();
    const deleteLinks = epletValidationApi.useDeleteLinks();

    const [confirmRemoval, setConfirmRemoval] = React.useState(false);
    const [isWorking, setIsWorking] = React.useState(false);
    const [failure, setFailure] = React.useState<string | null>(null);

    const eplet = detail.response?.payload;
    const alleles = eplet?.alleles ?? [];

    const toAdd = alleles.filter(x => !x.isLinked && x.isMatchedOnGene && x.isMatchedOnResidue);
    const toRemove = alleles.filter(x => x.isLinked && !(x.isMatchedOnGene && x.isMatchedOnResidue));
    const valid = alleles.filter(x => x.isLinked && x.isMatchedOnGene && x.isMatchedOnResidue);

    const removalShare = eplet && eplet.linkedCount > 0 ? toRemove.length / eplet.linkedCount : 0;
    const isBulkRemoval = removalShare >= BULK_REMOVAL_WARNING_SHARE;
    const isBusy = isWorking || createLinks.isExecuting || deleteLinks.isExecuting;

    const runAndReload = async (operation: () => Promise<ApiResponse<unknown>>, onFailure: string) => {
        setIsWorking(true);
        setFailure(null);

        try {
            const result = await operation();

            if (!result.isSuccess) setFailure(result.message ?? onFailure);

            // A revalidating query shows no spinner, so raise one for the reload.
            await dispatch(AppActionCreators.showLoadingAsync('Re-checking this eplet...'));
            // The list is a separate cache entry and keeps the pre-change counts until evicted.
            await Promise.all([detail.refresh(), invalidateEpletSummaries()]);
        } finally {
            dispatch(AppActionCreators.hideLoading());
            setIsWorking(false);
        }
    };

    const runAddAll = () => {
        if (epletId === null || toAdd.length === 0) return;

        return runAndReload(
            () => createLinks.execute({ id: epletId, alleleIds: toAdd.map(x => x.id) }),
            'The links could not be added.');
    };

    const runDeleteAll = () => {
        if (epletId === null || toRemove.length === 0) return;

        setConfirmRemoval(false);

        return runAndReload(
            () => deleteLinks.execute({ id: epletId, alleleIds: toRemove.map(x => x.id) }),
            'The links could not be removed.');
    };

    return (
        <div className='container-fluid' style={{ paddingTop: 20 }}>
            <p><Link to='/admin/hla/eplet'>&larr; Back to eplet validation</Link></p>

            {detail.status === ApiStatus.Error && (
                <div className='alert alert-danger'>
                    {detail.response?.message ?? 'This eplet could not be loaded.'}
                </div>
            )}

            {detail.status === ApiStatus.Loaded && eplet && (
                <>
                    <h2>
                        {eplet.name}{' '}
                        <small>{eplet.hlaGeneGroup} &middot; <code>{eplet.residue}</code></small>
                    </h2>

                    <div className={eplet.hasOnlyValidatedAlleles ? 'alert alert-success' : 'alert alert-warning'}>
                        {eplet.hasOnlyValidatedAlleles
                            ? <span>This eplet reconciles. All {eplet.linkedCount} links are valid and none are missing.</span>
                            : <span>
                                <strong>{eplet.linkedCount}</strong> linked &middot;{' '}
                                <strong>{eplet.missingCount}</strong> to add &middot;{' '}
                                <strong>{eplet.invalidCount}</strong> to remove
                            </span>}
                    </div>

                    {failure && <div className='alert alert-danger'>{failure}</div>}

                    <div style={{ marginBottom: 20 }}>
                        <button className='btn btn-primary'
                            disabled={isBusy || toAdd.length === 0}
                            onClick={runAddAll}>
                            Add All ({toAdd.length})
                        </button>
                        {' '}
                        <button className='btn btn-danger'
                            disabled={isBusy || toRemove.length === 0}
                            onClick={() => (isBulkRemoval ? setConfirmRemoval(true) : runDeleteAll())}>
                            Delete All ({toRemove.length})
                        </button>
                    </div>

                    {confirmRemoval && (
                        <div className='alert alert-danger'>
                            <p>
                                <strong>This removes {toRemove.length} of {eplet.linkedCount} links
                                ({Math.round(removalShare * 100)}%).</strong>
                            </p>
                            <p>
                                Losing most of an eplet&rsquo;s alleles at once usually means the polymorphic
                                residue was read wrongly rather than that the links drifted. Check{' '}
                                <code>{eplet.residue}</code> against epregistry before continuing &mdash;
                                a residue using <code>|</code> means the alternatives are OR&rsquo;d, not combined.
                            </p>
                            <button className='btn btn-danger' disabled={isBusy} onClick={runDeleteAll}>
                                Yes, remove {toRemove.length} links
                            </button>
                            {' '}
                            <button className='btn btn-default' disabled={isBusy} onClick={() => setConfirmRemoval(false)}>
                                Cancel
                            </button>
                        </div>
                    )}

                    {toAdd.length > 0 && (
                        <>
                            <h4 className='text-danger'>Missing links &mdash; residue matches but no link exists ({toAdd.length})</h4>
                            <AlleleTable alleles={toAdd} />
                        </>
                    )}

                    {toRemove.length > 0 && (
                        <>
                            <h4 className='text-danger'>Invalid links &mdash; linked but not justified ({toRemove.length})</h4>
                            <AlleleTable alleles={toRemove} showReason />
                        </>
                    )}

                    <h4>Valid links ({valid.length})</h4>
                    <AlleleTable alleles={valid} />
                </>
            )}
        </div>
    );
};
